import is from '@sindresorhus/is';
import { useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent } from '~/shared/data-contract/job-profile';
import { jobProfileSkillSchema } from '~/shared/data-contract/skill';
import { languageCodeSchema } from '~/shared/internationalization';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';

import type { ChangeProposalCompareProfile, ChangeProposalCompareTask } from '../../data-contract/chat';
import { useChangeProposalCompare } from '../api/use-change-proposal-compare';
import { DraftBadge } from '../components/DraftBadge';
import { JSA_COMPARE_PROPOSAL_VERSION } from '../url-tile-payloads';

export const changeProposalCompareProfileToJobProfileComponent = (
    profile: ChangeProposalCompareProfile,
): JobProfileComponent => ({
    id: profile.id,
    competencies: new Map(profile.competencies.map((competency) => [competency.id, { ...competency, titles: [] }])),
    language: languageCodeSchema.parse(profile.language),
    sectionTags: profile.sectionTags,
    skills: profile.skills.map((skill) =>
        jobProfileSkillSchema.parse({
            id: skill.id,
            ...(is.nullOrUndefined(skill.competencyId) ? {} : { competencyId: skill.competencyId }),
            level: skill.level,
            // The compare payload may omit the skill type; hard is the least
            // surprising rendering fallback for an unknown type.
            skillType: skill.type ?? 'hard',
            title: skill.title,
            titles: [],
        }),
    ),
    structured: profile.structured,
    title: profile.title,
    translations: profile.translations.map((translation) => ({
        language: translation.language,
        structured: translation.structured,
        title: translation.title,
    })),
    updatedAt: profile.updatedAt,
});

// Compare-tile view of a chat change proposal: the frozen proposed profile in
// JobProfileComponent shape plus the synthetic version-select entry it appears as.
export type ProposalCompareTasks = {
    readonly left: readonly ChangeProposalCompareTask[];
    readonly right: readonly ChangeProposalCompareTask[];
    readonly skillTitles: ReadonlyMap<string, string>;
};

// The task section shows only when the snapshot carries tasks on at least one side.
export const proposalCompareTasks = (data: {
    readonly current: ChangeProposalCompareProfile;
    readonly proposed: ChangeProposalCompareProfile;
}): ProposalCompareTasks | undefined => {
    const left = data.current.tasks ?? [];
    const right = data.proposed.tasks ?? [];
    if (left.length === 0 && right.length === 0) return undefined;
    return {
        left,
        right,
        skillTitles: new Map([...data.current.skills, ...data.proposed.skills].map(({ id, title }) => [id, title])),
    };
};

export const useProposalCompare = (
    proposalBlockId: string | undefined,
): {
    readonly hasProposalError: boolean;
    readonly proposalTasks: ProposalCompareTasks | undefined;
    readonly proposalVersion: VersionSelect | null;
    readonly proposedProfile: JobProfileComponent | undefined;
} => {
    const translate = useTranslate();
    const isProposalMode = is.nonEmptyString(proposalBlockId);

    const { data, isError } = useChangeProposalCompare({
        blockId: proposalBlockId ?? '',
        enabled: isProposalMode,
    });

    const proposedProfile = useMemo(
        () => (is.undefined(data) ? undefined : changeProposalCompareProfileToJobProfileComponent(data.proposed)),
        [data],
    );

    const proposalTasks = useMemo(() => (is.undefined(data) ? undefined : proposalCompareTasks(data)), [data]);

    if (!isProposalMode) {
        return { hasProposalError: false, proposalTasks: undefined, proposalVersion: null, proposedProfile: undefined };
    }

    return {
        hasProposalError: isError,
        proposalTasks,
        proposalVersion: {
            id: JSA_COMPARE_PROPOSAL_VERSION,
            author: '',
            badge: <DraftBadge />,
            title: translate('pages.job-architecture-v2.job-profile-compare-proposal'),
        },
        proposedProfile,
    };
};
